import * as React from "react"
import { useNavigate } from "react-router-dom"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { AddClientForm } from "@/components/gap-clients/add-client-form"
import { ClientEditPanel } from "@/components/gap-clients/client-edit-panel"
import { DeleteClientDialog } from "@/components/gap-clients/delete-client-dialog"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { SortableHeader, type SortDirection } from "@/components/shared/sortable-header"
import { Button } from "@/components/ui/button"
import { ArrowRight, Users, Pencil, Trash2 } from "lucide-react"
import { useClients, useCreateClient, useUpdateClient, useDeleteClient } from "@/hooks/use-gap-clients"
import { ApiErrorState } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { useToast } from "@/hooks/use-toast"
import { formatDateTimeWithZone } from "@/lib/utils"
import type { GapClientConfig, CreateGapClientRequest, UpdateGapClientRequest } from "@/types/gap-client"

type SortColumn = "clientId" | "venueId" | "necMatchScoreThreshold" | "featureFlagUserInstructions" | "lastUpdated"

function getClientFilterId(clientId: string): string {
  // Use the clientId directly as the filter ID
  return clientId
}

export function GapClientsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: clients, error, isLoading, mutate: refetchClients } = useClients()
  const createClient = useCreateClient()
  const updateClient = useUpdateClient()
  const deleteClient = useDeleteClient()
  const [addClientOpen, setAddClientOpen] = React.useState(false)
  const [editingClient, setEditingClient] = React.useState<GapClientConfig | null>(null)
  const [isEditPanelOpen, setIsEditPanelOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [clientToDelete, setClientToDelete] = React.useState<GapClientConfig | null>(null)
  const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc")

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const sortedClients = React.useMemo(() => {
    if (!clients) return []
    if (!sortColumn) return clients

    return [...clients].sort((a, b) => {
      let comparison = 0
      switch (sortColumn) {
        case "clientId":
          comparison = a.clientId.localeCompare(b.clientId)
          break
        case "venueId":
          comparison = a.venueId - b.venueId
          break
        case "necMatchScoreThreshold":
          comparison = a.necMatchScoreThreshold - b.necMatchScoreThreshold
          break
        case "featureFlagUserInstructions":
          comparison = (a.featureFlagUserInstructions ? 1 : 0) - (b.featureFlagUserInstructions ? 1 : 0)
          break
        case "lastUpdated":
          comparison = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()
          break
      }
      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [clients, sortColumn, sortDirection])

  const handleViewDevices = (client: GapClientConfig) => {
    // Navigate to devices page with client filter
    const filterId = getClientFilterId(client.clientId)
    navigate(`/go-ahead-pay/devices?clients=${filterId}`)
  }

  const handleClientCreated = async (data: CreateGapClientRequest) => {
    try {
      await createClient.trigger(data)
      setAddClientOpen(false)
      toast({
        variant: "success",
        title: "Client created successfully",
        description: `Client ${data.clientId} has been added to the system.`,
      })
      refetchClients()
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to create client",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleEditClient = (client: GapClientConfig) => {
    setEditingClient(client)
    setIsEditPanelOpen(true)
  }

  const handleSaveClient = async (clientId: string, data: UpdateGapClientRequest) => {
    try {
      await updateClient.trigger({ clientId, data })
      setIsEditPanelOpen(false)
      toast({
        variant: "success",
        title: "Client updated successfully",
        description: `Client ${clientId} has been updated.`,
      })
      refetchClients()
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update client",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  const handleDeleteClick = (client: GapClientConfig) => {
    setClientToDelete(client)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return

    try {
      await deleteClient.trigger(clientToDelete.clientId)
      setDeleteDialogOpen(false)
      toast({
        variant: "success",
        title: "Client deleted",
        description: `${clientToDelete.clientId} has been permanently removed.`,
      })
      refetchClients()
      setClientToDelete(null)
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to delete client",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  if (isLoading) {
    return (
      <>
        <DashboardHeader onAddClient={() => setAddClientOpen(true)} />
        <main className="container mx-auto px-4 py-6">
          <DevicesTableSkeleton />
        </main>
      </>
    )
  }

  if (error) {
    return (
      <>
        <DashboardHeader onAddClient={() => setAddClientOpen(true)} />
        <main className="container mx-auto px-4 py-6">
          <ApiErrorState onRetry={() => window.location.reload()} />
        </main>
      </>
    )
  }

  if (!clients || clients.length === 0) {
    return (
      <>
        <DashboardHeader onAddClient={() => setAddClientOpen(true)} />
        <main className="container mx-auto px-4 py-6">
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No clients found</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm">There are no clients configured in the system.</p>
              <Button onClick={() => setAddClientOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                Add Client
              </Button>
            </CardContent>
          </Card>
        </main>
        <AddClientForm
          open={addClientOpen}
          onOpenChange={setAddClientOpen}
          onSubmit={handleClientCreated}
          isSubmitting={createClient.isMutating}
        />
      </>
    )
  }

  return (
    <>
      <DashboardHeader onAddClient={() => setAddClientOpen(true)} />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Clients</h1>
          <p className="text-sm text-muted-foreground">Manage and view all clients in the system</p>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <div className="table-scroll-x overflow-x-auto">
              <Table className="min-w-max w-full">
                <TableHeader>
                  <TableRow className="border-border hover:bg-card">
                    <SortableHeader
                      label="Client ID"
                      sortKey="clientId"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Venue ID"
                      sortKey="venueId"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="NEC Threshold"
                      sortKey="necMatchScoreThreshold"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="User Instructions"
                      sortKey="featureFlagUserInstructions"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Last Updated"
                      sortKey="lastUpdated"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <TableHead className="text-muted-foreground font-medium w-32">Actions</TableHead>
                    <TableHead className="text-muted-foreground font-medium w-16 sticky right-0 bg-card z-10">Edit / Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client, index) => (
                      <TableRow
                        key={client.clientId}
                        className={`border-border transition-colors group ${
                          index % 2 === 1 ? "bg-muted/30 hover:bg-muted/50" : "hover:bg-muted/50"
                        }`}
                      >
                        <TableCell>
                          <div className="font-medium text-foreground">{client.clientId}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-foreground">{client.venueId}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-foreground">{client.necMatchScoreThreshold}</div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              client.featureFlagUserInstructions
                                ? "bg-success/20 text-success"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {client.featureFlagUserInstructions ? "Enabled" : "Disabled"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-foreground">
                            {formatDateTimeWithZone(client.lastUpdated)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDevices(client)}
                            className="bg-card border-input text-foreground hover:bg-secondary hover:text-foreground"
                          >
                            View Devices
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </TableCell>
                        <TableCell className="sticky right-0 bg-card z-10 min-w-16">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditClient(client)}
                              className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Edit client</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(client)}
                              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete client</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
      <AddClientForm
        open={addClientOpen}
        onOpenChange={setAddClientOpen}
        onSubmit={handleClientCreated}
        isSubmitting={createClient.isMutating}
      />
      <ClientEditPanel
        client={editingClient}
        open={isEditPanelOpen}
        onOpenChange={setIsEditPanelOpen}
        onSave={handleSaveClient}
        isSaving={updateClient.isMutating}
      />
      <DeleteClientDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        clientId={clientToDelete?.clientId || ""}
        onConfirm={handleConfirmDelete}
        isLoading={deleteClient.isMutating}
      />
    </>
  )
}
